import { InstallatoreCard } from "./InstallatoreCard";
import { PuliziaCard } from "@/components/PuliziaCard";
import { PuliziaStorico } from "./PuliziaStorico";
import { InstallaBanner, InstallaPill, Tutorial, segnaInstallata, invitoMomento } from "./InstallaHome";
import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { VersioneProBadge } from "./VersioneProBadge";
import { Loader2, Leaf, Trees, Zap, CheckCircle2, AlertCircle, Home, BarChart3, Cpu, Settings, Sparkles, Monitor, FileText, Wallet, Download, ChevronDown } from "lucide-react";
import { ClienteImpostazioni } from "./ClienteImpostazioni";
import { ClienteReport } from "./ClienteReport";
import { toast } from "sonner";
import { SkyScene } from "./SkyScene";
import { ClienteCharts, Card } from "./ClienteCharts";
import { BrandBar } from "./BrandBar";
import { LocalitaInvite } from "./LocalitaInvite";
import { ClienteLocalita } from "./ClienteLocalita";
import { Tiles } from "./ClienteTiles";
import { ScreenMode } from "./ScreenMode";
import { RiepilogoCard } from "./RiepilogoCard";
import { AllarmiCliente } from "./AllarmiCliente";
import { ClienteExportDialog } from "./ClienteExport";
import { ClienteComponente } from "./ClienteComponente";
import { VestaVoiceOverlay } from "@/pages/vesta/VestaVoice";
import { VestaSlot, RACCONTI_VESTA } from "./VestaSlot";
import { ClienteEconomia } from "./ClienteEconomia";
import VestaFlame from "@/pages/vesta/VestaFlame";
import { kwh, n, pw } from "./clienteApi";

const REFRESH_MS = 5 * 60 * 1000;
const FONT_EXTRA = "@keyframes cvNews{0%,100%{box-shadow:0 0 0 0 rgba(193,127,89,.7)}50%{box-shadow:0 0 0 10px rgba(193,127,89,0)}}"
  + ".vesta-glow{box-shadow:0 0 0 3px #C17F59,0 0 34px rgba(193,127,89,.55)!important;transition:box-shadow .4s ease}";
const fontCss = () => ".cliente-font{font-family:Arial,Helvetica,sans-serif}" + FONT_EXTRA;

// Vesta "indica" la parte di pagina di cui sta parlando
const FOCUS = { batteria: ["casa", "tile-batteria"], consumi: ["casa", "tile-casa"], rete: ["casa", "tile-rete"], ambiente: ["casa", "cliente-vita"], produzione: ["casa", "cliente-scene"], grafico: ["energia", "cliente-panoramica"] };
function useVestaFocus(box, setTab) {
  useEffect(() => {
    const on = (e) => {
      const f = FOCUS[e.detail];
      if (!f) return;
      setTab(f[0]);
      setTimeout(() => {
        const el = box.current?.querySelector(`[data-testid="${f[1]}"]`);
        if (!el) return;
        el.scrollIntoView({ behavior: "smooth", block: "start" });
        el.classList.add("vesta-glow");
        setTimeout(() => el.classList.remove("vesta-glow"), 6000);
      }, 200);
    };
    window.addEventListener("vesta-focus", on);
    return () => window.removeEventListener("vesta-focus", on);
  }, [box, setTab]);
}

const Stato = ({ imp }) => {
  const ok = imp.stato === "ok", I = ok ? CheckCircle2 : AlertCircle;
  return <span className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-bold ${ok ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-700"}`} data-testid="cliente-stato"><I className="h-4 w-4" />{imp.stato_frase}</span>;
};

export const frase = (imp) => {
  if (imp.autosufficienza_pct != null && imp.oggi_consumata) return `Oggi il sole ha coperto il ${n(imp.autosufficienza_pct, 0)}% dei tuoi consumi.`;
  if (imp.oggi_kwh > 0) return `Oggi il tuo impianto ha già prodotto ${kwh(imp.oggi_kwh)} di energia pulita.`;
  return `Finora il tuo impianto ha prodotto ${kwh(imp.vita?.energia_kwh)}: un bel risultato per l'ambiente.`;
};

export const Vita = ({ v }) => (
  <Card title="Il tuo contributo" testid="cliente-vita">
    <div className="grid grid-cols-3 gap-2.5 text-center">
      {[[Zap, "Energia prodotta", kwh(v.energia_kwh), "#FEF3C7", "#B45309", "vita-energia"], [Leaf, "CO₂ evitata", v.co2_kg >= 1000 ? `${n(v.co2_kg / 1000, 1)} t` : `${n(v.co2_kg, 0)} kg`, "#DCFCE7", "#15803D", "vita-co2"],
        [Trees, "Alberi equivalenti", n(v.alberi, 0), "#ECFCCB", "#4D7C0F", "vita-alberi"]].map(([I, l, val, bg, c, id]) => (
        <div key={l} className="rounded-2xl p-3" style={{ background: bg }} data-testid={id}><I className="mx-auto h-5 w-5" style={{ color: c }} /><b className="mt-1 block text-lg text-slate-800">{val}</b><span className="text-[11px] text-slate-600">{l}</span></div>
      ))}
    </div>
    <p className="mt-2 text-[11px] text-slate-400">CO₂ e alberi sono stime indicative.</p>
  </Card>
);

// pulizia pannelli: senza località mostra l'invito a indicarla; durante l'analisi ricarica ogni 15 s
const ClientePulizia = ({ imp, src }) => {
  const [d, setD] = useState(imp.pulizia);
  useEffect(() => setD(imp.pulizia), [imp.pulizia]);
  useEffect(() => {
    if (d?.spia !== "in_corso") return undefined;
    const t = setTimeout(() => src.impianto().then((r) => setD(r.pulizia)).catch(() => {}), 15000);
    return () => clearTimeout(t);
  }, [d, src]);
  const [graf, setGraf] = useState(false);
  const apribile = ["pulito", "osservare", "pulizia", "da_verificare"].includes(d?.spia);
  return (
    <>
    {apribile && <PuliziaStorico open={graf} onClose={() => setGraf(false)} src={src} d={d} />}
    <PuliziaCard d={d} onClick={apribile ? () => setGraf(true) : undefined}>
      {d?.spia === "senza_posizione" && src.localita && <div className="mt-3"><LocalitaInvite azienda={imp?.tipo === "azienda"} src={src} onSaved={() => src.impianto().then((r) => setD(r.pulizia))} /></div>}
    </PuliziaCard>
    </>
  );
};

const INV_STATO = {
  ok: { c: "#10B981", bg: "bg-emerald-50", t: "text-emerald-700", l: "In funzione" },
  avviso: { c: "#F59E0B", bg: "bg-amber-50", t: "text-amber-700", l: "Avviso" },
  guasto: { c: "#EF4444", bg: "bg-red-50", t: "text-red-700", l: "Guasto" },
};
const InverterMonitor = ({ inverters, onPick }) => {
  const [open, setOpen] = useState(false);
  if (!inverters?.length) return null;
  const attivi = inverters.filter((v) => v.stato !== "ok").length;
  return (
    <Card testid="cliente-inverter-monitor">
      <button type="button" onClick={() => setOpen((o) => !o)} className="flex w-full items-center justify-between gap-2" data-testid="cliente-inverter-toggle">
        <span className="text-left">
          <b className="block text-base font-extrabold text-slate-800">I tuoi inverter</b>
          <span className="text-sm text-slate-500">{inverters.length} inverter · {attivi ? `${attivi} da controllare` : "tutti regolari"}</span>
        </span>
        <ChevronDown className={`h-5 w-5 shrink-0 text-slate-400 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && <div className="mt-3 space-y-2.5">
        {inverters.map((v) => { const s = INV_STATO[v.stato] || INV_STATO.ok; const notte = (v.potenza_w || 0) <= 0; return (
          <button type="button" key={v.sn} onClick={() => onPick?.(v.sn)} className="w-full rounded-2xl bg-slate-50 p-3 text-left transition-colors hover:bg-sky-50" data-testid={`cliente-inverter-${v.sn}`}>
            <div className="flex items-center gap-2.5">
              <span className="h-2.5 w-2.5 shrink-0 rounded-full" style={{ background: s.c }} />
              <div className="min-w-0 flex-1">
                <b className="block truncate text-sm text-slate-800">Inverter · {v.sn}</b>
                <span className="block truncate text-[11px] text-slate-400">{v.model}</span>
              </div>
              <div className="shrink-0 text-right">
                <b className="block text-sm text-slate-800" data-testid={`cliente-inverter-pot-${v.sn}`}>{notte ? "0 W" : pw(v.potenza_w)}</b>
                <span className="block text-[11px] text-slate-400">oggi {kwh(v.oggi_kwh)}</span>
              </div>
            </div>
            <div className="mt-2 flex flex-wrap items-center gap-1.5">
              <span className={`rounded-full px-2 py-0.5 text-[11px] font-bold ${s.bg} ${s.t}`} data-testid={`cliente-inverter-stato-${v.sn}`}>{s.l}</span>
              {(v.allarmi || []).map((a, i) => (
                <span key={i} className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${a.grave ? "bg-red-50 text-red-700" : "bg-amber-50 text-amber-700"}`} data-testid={`cliente-inverter-allarme-${v.sn}-${i}`}>{a.titolo}</span>
              ))}
              <span className="ml-auto text-[11px] font-semibold text-sky-600">Confronta in Energia →</span>
            </div>
          </button>
        ); })}
      </div>}
    </Card>
  );
};
const Impianto = ({ imp, src, onInverter, reload }) => {
  const [sel, setSel] = useState(null);
  const [openComp, setOpenComp] = useState(false);
  const nComp = imp.componenti?.length || 0;
  return (
  <div className="space-y-4" data-testid="cliente-tab-impianto-panel">
    <ClienteComponente c={sel} src={src} onClose={() => setSel(null)} />
    {imp.impianto_geo && <ClienteLocalita imp={imp} src={src} reload={reload} />}
    <Card testid="cliente-componenti">
      <button type="button" onClick={() => setOpenComp((o) => !o)} className="flex w-full items-center justify-between gap-2" data-testid="cliente-componenti-toggle">
        <span className="text-left">
          <b className="block text-base font-extrabold text-slate-800">Il mio impianto</b>
          <span className="text-sm text-slate-500">{n(imp.kwp, 1)} kWp{imp.installato_il ? ` · attivo dal ${new Date(imp.installato_il).toLocaleDateString("it-IT")}` : ""}{nComp ? ` · ${nComp} componenti` : ""}</span>
        </span>
        {nComp > 0 && <ChevronDown className={`h-5 w-5 shrink-0 text-slate-400 transition-transform ${openComp ? "rotate-180" : ""}`} />}
      </button>
      {openComp && <div className="mt-3 space-y-2.5">
        {(imp.componenti || []).map((c) => (
          <button type="button" onClick={() => c.codice && src.componente && setSel(c)} key={c.tipo + c.codice} className="flex w-full items-center gap-3 rounded-2xl bg-slate-50 p-3 text-left transition-colors hover:bg-sky-50" data-testid={`cliente-comp-${c.tipo.split(" ")[0].toLowerCase()}`}>
            <span className="flex h-16 w-16 shrink-0 items-center justify-center overflow-hidden rounded-xl bg-white">{c.foto ? <img src={encodeURI(decodeURI(c.foto))} alt={c.codice} className="h-full w-full object-contain" /> : <Cpu className="h-6 w-6 text-slate-300" />}</span>
            <span className="min-w-0"><b className="block text-[11px] uppercase tracking-wider text-slate-400">{c.tipo}{c.quantita ? ` × ${c.quantita}` : ""}</b><b className="block truncate text-slate-800">{c.codice}</b>
              {c.descrizione && <span className="line-clamp-2 text-xs text-slate-500">{c.descrizione}</span>}</span>
          </button>
        ))}
        {!imp.componenti?.length && <p className="text-sm text-slate-500">I componenti saranno visibili appena disponibili.</p>}
      </div>}
    </Card>
    <InverterMonitor inverters={imp.inverters} onPick={onInverter} />
    {src.riepilogo && <RiepilogoCard src={src} />}
    <Card title="Il tuo installatore" testid="cliente-installatore">
      <InstallatoreCard i={imp.installatore} />
    </Card>
    <Tutorial />
  </div>
  );
};

function TabBar({ tab, setTab, onVesta, novita, azienda, economia }) {
  const B = ({ id, I, l }) => (
    <button type="button" onClick={() => setTab(id)} data-testid={`cliente-nav-${id}`} className={`flex flex-1 flex-col items-center gap-0.5 py-2 text-[11px] font-bold transition-colors ${tab === id ? "text-sky-600" : "text-slate-400"}`}>
      <I className="h-5 w-5" />{l}</button>
  );
  return (
    <nav className="sticky bottom-0 z-20 mx-auto flex w-full max-w-2xl items-end rounded-t-3xl bg-white/95 px-2 pb-[max(env(safe-area-inset-bottom),6px)] shadow-[0_-6px_24px_rgba(15,23,42,.08)] backdrop-blur" data-testid="cliente-tabbar">
      <B id="casa" I={Home} l={azienda ? "Azienda" : "Casa"} /><B id="energia" I={BarChart3} l="Energia" />
      <button type="button" onClick={onVesta} className="-mt-6 flex flex-1 flex-col items-center text-[11px] font-bold text-[#C17F59]" data-testid="cliente-vesta-open">
        <span className={`relative rounded-full bg-black p-1 shadow-lg ring-4 ring-white ${novita ? "animate-[cvNews_1.6s_ease-in-out_infinite]" : ""}`}><VestaFlame size={46} />
          {novita && <span className="absolute -right-0.5 -top-0.5 h-3.5 w-3.5 rounded-full bg-amber-400 ring-2 ring-white" data-testid="cliente-vesta-novita" />}</span>{novita ? "Ha una novità" : "Vesta"}</button>
      {economia && <B id="economia" I={Wallet} l="Economia" />}
      <B id="impianto" I={Cpu} l="Impianto" />
    </nav>
  );
}

export function ClienteView({ src, header, embed, impostazioniInstallatore }) {
  const [imp, setImp] = useState(null);
  const [err, setErr] = useState(null);
  const [vesta, setVesta] = useState(false);
  const [seme, setSeme] = useState(null);
  const [tab, setTab] = useState("casa");
  const box = useRef(null);
  const [tick, setTick] = useState(0);
  const [schermo, setSchermo] = useState(false);
  const [impOpen, setImpOpen] = useState(false);
  const [grande, setGrande] = useState(() => { try { return localStorage.getItem("cliente-testo-grande") === "1"; } catch { return false; } });
  useEffect(() => { try { localStorage.setItem("cliente-testo-grande", grande ? "1" : "0"); } catch { /* ignora */ } }, [grande]);
  const [report, setReport] = useState(false);
  const [invSel, setInvSel] = useState([]);
  useEffect(() => {
    if (!imp) return;
    segnaInstallata(imp.app_installata);
    if (imp.record_oggi) invitoMomento("Oggi record di produzione! Vuoi ritrovarlo sempre qui? Installa l'app in 2 tocchi");
  }, [imp?.app_installata, imp?.record_oggi]); // eslint-disable-line react-hooks/exhaustive-deps
  const [novita, setNovita] = useState(false);
  useVestaFocus(box, setTab);
  const load = () => {
    if (!embed) src.notizie?.().then((d) => setNovita(!!d.ha)).catch(() => {});
    return src.impianto().then((d) => { setImp(d); setErr(null); }).catch((e) => setImp((x) => { if (!x) setErr(e.response?.data?.detail || "Dati non disponibili, riprova tra poco"); return x; }));
  };
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const ultimo = useRef(Date.now());
  const ricarica = () => { ultimo.current = Date.now(); load(); setTick((x) => x + 1); };
  useEffect(() => {
    const t = setInterval(() => { if (document.visibilityState === "visible") ricarica(); }, REFRESH_MS);
    // rientro nell'app (o nella scheda) dopo più di un minuto: aggiorna subito
    const rientro = () => { if (document.visibilityState === "visible" && Date.now() - ultimo.current > 60000) ricarica(); };
    document.addEventListener("visibilitychange", rientro);
    window.addEventListener("pageshow", rientro);
    window.addEventListener("focus", rientro);
    return () => { clearInterval(t); document.removeEventListener("visibilitychange", rientro); window.removeEventListener("pageshow", rientro); window.removeEventListener("focus", rientro); };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const [exportOpen, setExportOpen] = useState(false);
  const share = async () => {
    const text = `Oggi il mio impianto fotovoltaico TORRI ha prodotto ${kwh(imp.oggi_kwh)} e finora ha evitato ${n(imp.vita.co2_kg, 0)} kg di CO₂.`;
    let url;
    try { url = `${window.location.origin}/api/l/s/${(await src.condividi()).token}`; } catch { toast.error("Link non disponibile, condivido solo il testo"); }
    try {
      if (navigator.share) await navigator.share({ title: "Il mio impianto solare", text, url });
      else { await navigator.clipboard.writeText(url ? `${text}\n${url}` : text); toast.success("Copiato! Il link resta visibile per 24 ore"); }
      invitoMomento("Vuoi ritrovarlo sempre qui? Installa l'app in 2 tocchi");
    } catch { /* annullato */ }
  };
  return (
    <div ref={box} style={{ zoom: grande ? 1.18 : 1 }} className={`cliente-font flex flex-col bg-slate-50 text-slate-800 ${embed ? "overflow-hidden rounded-3xl" : "min-h-[100dvh]"}`} data-testid="cliente-view">
      <style>{fontCss()}</style>
      {header}
      <div className="mx-auto w-full max-w-2xl flex-1 space-y-4 p-4 pb-8">
        {err ? <p className="py-20 text-center text-slate-500" data-testid="cliente-error">{err}</p> : !imp ? <div className="flex justify-center py-24"><Loader2 className="h-8 w-8 animate-spin text-sky-500" /></div> : <>
          <BrandBar logo={imp.installatore?.logo} nome={imp.installatore?.nome} clienteLogo={imp.cliente_logo} clienteLogoBg={imp.cliente_logo_bg} />
          {report && <ClienteReport src={src} pro={imp.pro} onClose={() => setReport(false)} />}
          <div className="space-y-2.5">
            <div className="flex items-start justify-between gap-3">
              <h1 className="min-w-0 flex-1 truncate text-2xl font-extrabold text-slate-900" data-testid="cliente-nome">{imp.nome}</h1>
              {!embed && <div className="flex shrink-0 gap-1.5">
            {imp.pro && src.exportCsv && <button type="button" onClick={() => setExportOpen(true)} className="rounded-full bg-white p-2.5 text-sky-600 shadow-sm ring-1 ring-slate-100 transition-transform hover:scale-105" aria-label="Esporta dati" title="Esporta dati (CSV)" data-testid="cliente-export"><Download className="h-5 w-5" /></button>}
            {src.reportLista && <button type="button" onClick={() => setReport(true)} className="rounded-full bg-white p-2.5 text-sky-600 shadow-sm ring-1 ring-slate-100 transition-transform hover:scale-105" aria-label="Report mensili" title="Report mensili" data-testid="cliente-report-open"><FileText className="h-5 w-5" /></button>}
            <button type="button" onClick={() => setSchermo(true)} className="rounded-full bg-white p-2.5 text-sky-600 shadow-sm ring-1 ring-slate-100 transition-transform hover:scale-105" aria-label="Modalità schermo" title="Modalità schermo (TV e monitor)" data-testid="cliente-schermo-open"><Monitor className="h-5 w-5" /></button>
            <button type="button" onClick={() => setImpOpen(true)} className="shrink-0 rounded-full bg-white p-2.5 text-sky-600 shadow-sm ring-1 ring-slate-100 transition-transform hover:scale-105" aria-label="Impostazioni" title="Impostazioni" data-testid="cliente-impostazioni-open"><Settings className="h-5 w-5" /></button>
            </div>}
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <Stato imp={imp} />
              <VersioneProBadge pro={imp.pro} />
            </div>
            {!embed && <InstallaPill />}
          </div>
          {tab === "casa" && <>
            {!embed && imp.chiedi_localita && src.localita && <LocalitaInvite azienda={imp.tipo === "azienda"} src={src} onSaved={() => { toast.success("Località salvata, grazie!"); load(); }} />}
            <VestaSlot onInfo={() => { setSeme(RACCONTI_VESTA); setVesta(true); }} />
            <SkyScene imp={imp} src={src} />
            <Tiles imp={imp} src={src} />
            <AllarmiCliente src={src} list={imp.allarmi || []} pro={imp.pro} />
            <div className="flex items-start gap-3 rounded-3xl bg-gradient-to-r from-amber-50 to-emerald-50 p-4 ring-1 ring-amber-100" data-testid="cliente-frase">
              <Sparkles className="mt-0.5 h-5 w-5 shrink-0 text-amber-500" /><p className="text-sm font-semibold text-slate-700">{frase(imp)}</p>
            </div>
            <ClientePulizia imp={imp} src={src} />
            <Vita v={imp.vita} />
          </>}
          {tab === "energia" && <ClienteCharts src={src} haBatteria={imp.ha_batteria} tick={tick} azienda={imp.tipo === "azienda"} inverters={imp.inverters} selInit={invSel} pro={imp.pro} />}
          {tab === "economia" && src.economia && <ClienteEconomia src={src} pro={imp.pro} />}
          {tab === "impianto" && <Impianto imp={imp} src={src} reload={load} onInverter={(sn) => { setInvSel([sn]); setTab("energia"); }} />}
        </>}
      </div>
      {imp && schermo && createPortal(<ScreenMode imp={imp} src={src} tick={tick} onClose={() => setSchermo(false)} />, box.current?.closest('[role="dialog"]') || document.body)}
      {imp && !schermo && !embed && <InstallaBanner />}
      {imp && !embed && <TabBar tab={tab} setTab={setTab} novita={novita} azienda={imp.tipo === "azienda"} economia={!!src.economia} onVesta={() => { setVesta(true); setNovita(false); }} />}
      {vesta && !embed && createPortal(<VestaVoiceOverlay src={src} racconti={seme} onClose={() => { setVesta(false); setSeme(null); }} />, box.current?.closest('[role="dialog"]') || document.body)}
      {imp && !embed && createPortal(<ClienteExportDialog open={exportOpen} onClose={() => setExportOpen(false)} src={src} nome={imp.nome} />, box.current?.closest('[role="dialog"]') || document.body)}
      {imp && impOpen && createPortal(<ClienteImpostazioni src={src} imp={imp} grande={grande} setGrande={setGrande} staff={impostazioniInstallatore} onShare={share} onClose={() => setImpOpen(false)} reload={load} />, box.current?.closest('[role="dialog"]') || document.body)}
    </div>
  );
}
